import React from "react";
import useOnClickOutside from "use-onclickoutside";

import Icon from "@ats/src/components/shared/Icon";

import styled from "@emotion/styled";
import { css } from "@emotion/react";
import { useSpring, animated } from "react-spring";

type Props = {
  hpos?: string;
  vpos?: string;
  text: string;
  icon?: string;
  caret?: boolean;
  styleType?: "outline" | "solid";
  testid?: string;
  testidPrefix?: string;
  children?: any;
  disabled?: boolean;
};

const menuMaxHeight = 300;
const menuMinHeight = 120;
const menuGutter = 24;

const TextDropdownMenu = ({
  hpos = "left",
  vpos = "bottom",
  text,
  icon,
  caret = true,
  styleType = "outline",
  testid,
  testidPrefix,
  children,
  disabled = false,
}: Props) => {
  const [dropdownIsVisible, setDropdownIsVisible] = React.useState(false);
  const [placement, setPlacement] = React.useState(vpos);
  const [maxHeight, setMaxHeight] = React.useState(menuMaxHeight);
  const ref = React.useRef(null);
  const dataTestIdPrefix = testidPrefix ? `${testidPrefix}-` : "";

  React.useLayoutEffect(() => {
    if (!dropdownIsVisible || ref.current == undefined) return;

    const triggerRect = ref.current.getBoundingClientRect();
    const spaceBelow = window.innerHeight - triggerRect.bottom - menuGutter;
    const spaceAbove = triggerRect.top - menuGutter;
    const preferredSpace = vpos === "top" ? spaceAbove : spaceBelow;
    const alternateSpace = vpos === "top" ? spaceBelow : spaceAbove;
    const fitsPreferred = preferredSpace >= menuMinHeight;
    const available = fitsPreferred ? preferredSpace : alternateSpace;

    setPlacement(fitsPreferred ? vpos : vpos === "top" ? "bottom" : "top");
    setMaxHeight(Math.max(menuMinHeight, Math.min(available, menuMaxHeight)));
  }, [dropdownIsVisible, vpos]);

  useOnClickOutside(ref, () => {
    handleClickOutside();
  });

  const handleClickOutside = () => {
    if (dropdownIsVisible) {
      setDropdownIsVisible(false);
    }
  };

  const toggleDropdownVisibility = (e) => {
    if (disabled) return;
    e.preventDefault();
    e.stopPropagation();
    setDropdownIsVisible(!dropdownIsVisible);
  };

  const closeDropdown = (e) => {
    e.stopPropagation();
    handleClickOutside();
  };

  const spring = useSpring({
    from: {
      opacity: 0,
      transform: "scale(0.95)",
      pointerEvents: "none",
    },
    to: {
      opacity: dropdownIsVisible ? 1 : 0,
      transform: dropdownIsVisible ? "scale(1)" : "scale(0.95)",
      pointerEvents: dropdownIsVisible ? "all" : "none",
    },
    config: {
      tension: 480,
      clamp: true,
    },
  });

  const Trigger = Triggers[styleType][`${icon ? "icon" : "plain"}-${caret ? "caret" : "flat"}`];

  return (
    <Styled.Container ref={ref} data-testid={testid || `${dataTestIdPrefix}text-dropdown-menu`}>
      <Trigger
        type="button"
        onClick={toggleDropdownVisibility}
        disabled={disabled}
        aria-haspopup="true"
        aria-expanded={dropdownIsVisible}
        data-testid={`${dataTestIdPrefix}text-dropdown-toggle-button`}
      >
        {icon && <Icon name={icon} />}
        <Styled.ButtonLabel>{text}</Styled.ButtonLabel>
        {caret &&
          (dropdownIsVisible ? (
            <Styled.OpenCaret>
              <Icon name="chevron-down" />
            </Styled.OpenCaret>
          ) : (
            <Styled.Caret>
              <Icon name="chevron-down" />
            </Styled.Caret>
          ))}
      </Trigger>

      <Styled.Dropdown
        style={spring}
        hpos={hpos}
        vpos={placement}
        onClick={closeDropdown}
        data-testid={`${dataTestIdPrefix}text-dropdown-menu-list`}
      >
        <Styled.DropdownContent
          style={{ "--menu-max-height": `${maxHeight}px` } as React.CSSProperties}
        >
          {children}
        </Styled.DropdownContent>
      </Styled.Dropdown>
    </Styled.Container>
  );
};

export default TextDropdownMenu;

/* Styled Components
======================================================= */
let Styled: any;
Styled = {};

Styled.Container = styled.div`
  label: TextDropdownMenu;
  position: relative;
  display: inline-block;
`;

const buttonShape = (t: any, hasIcon: boolean, hasCaret: boolean) => css`
  ${[t.h(8), t.text.sm, t.text.medium, t.rounded.sm]}
  box-sizing: border-box;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: ${t.spacing[2]};
  padding-left: ${hasIcon ? t.spacing[2] : t.spacing[3]};
  padding-right: ${hasCaret ? t.spacing[2] : t.spacing[3]};
  line-height: 1;
  white-space: nowrap;
  cursor: pointer;
  user-select: none;
  transition: background-color 0.2s ease, border-color 0.2s ease, box-shadow 0.2s ease;

  &:focus {
    outline: none;
    box-shadow: 0 0 0 2px ${t.dark ? t.color.gray[500] : t.color.gray[300]};
  }

  &[disabled] {
    opacity: 0.5;
    cursor: default;
  }

  svg {
    display: block;
    flex-shrink: 0;
    width: 16px;
    height: 16px;
  }
`;

const outlineSkin = (t: any) => css`
  color: ${t.dark ? t.color.gray[200] : t.color.black};
  background-color: ${t.dark ? "rgba(255, 255, 255, 0.07)" : t.color.white};
  border: 1px solid ${t.dark ? t.color.gray[800] : t.color.gray[200]};

  &:hover:not([disabled]) {
    background-color: ${t.dark ? t.color.gray[800] : t.color.gray[100]};
  }
`;

const solidSkin = (t: any) => css`
  color: ${t.dark ? t.color.gray[200] : t.color.black};
  background-color: ${t.dark ? t.color.gray[700] : t.color.gray[200]};
  border: 1px solid transparent;

  &:hover:not([disabled]) {
    background-color: ${t.dark ? t.color.gray[600] : t.color.gray[300]};
  }
`;

const trigger = (skin, hasIcon: boolean, hasCaret: boolean, name: string) =>
  styled.button((props: any) => {
    const t: any = props.theme;
    return css`
      label: TextDropdownMenu_${name};
      ${buttonShape(t, hasIcon, hasCaret)}
      ${skin(t)}
    `;
  });

const Triggers = {
  outline: {
    "icon-caret": trigger(outlineSkin, true, true, "OutlineIconCaret"),
    "icon-flat": trigger(outlineSkin, true, false, "OutlineIconFlat"),
    "plain-caret": trigger(outlineSkin, false, true, "OutlineCaret"),
    "plain-flat": trigger(outlineSkin, false, false, "OutlineFlat"),
  },
  solid: {
    "icon-caret": trigger(solidSkin, true, true, "SolidIconCaret"),
    "icon-flat": trigger(solidSkin, true, false, "SolidIconFlat"),
    "plain-caret": trigger(solidSkin, false, true, "SolidCaret"),
    "plain-flat": trigger(solidSkin, false, false, "SolidFlat"),
  },
};

Styled.ButtonLabel = styled.span`
  label: TextDropdownMenu_ButtonLabel;
  display: block;
  line-height: 1;
  margin-top: 1px;
`;

Styled.Caret = styled.span((props: any) => {
  const t: any = props.theme;
  return css`
    label: TextDropdownMenu_Caret;
    display: flex;
    color: ${t.dark ? t.color.gray[400] : t.color.gray[600]};

    svg {
      transition: transform 0.2s ease;
    }
  `;
});

Styled.OpenCaret = styled.span((props: any) => {
  const t: any = props.theme;
  return css`
    label: TextDropdownMenu_OpenCaret;
    display: flex;
    color: ${t.dark ? t.color.gray[400] : t.color.gray[600]};

    svg {
      transform: rotate(180deg);
      transition: transform 0.2s ease;
    }
  `;
});

Styled.Dropdown = styled(animated.div)((props: any) => {
  const t: any = props.theme;
  const modeStyles = t.dark
    ? css`
        background-color: ${t.color.gray[700]};
        border: 1px solid ${t.color.gray[600]};
      `
    : css`
        background-color: ${t.color.white};
        border: 1px solid ${t.color.gray[400]};
      `;

  return css`
    label: TextDropdownMenu_Dropdown;
    ${[t.rounded.sm, t.p(1)]};
    padding-bottom: 2px;

    ${props.hpos === "right" ? t.r(0) : t.l(0)};
    ${props.vpos === "top" ? t.b(10) : t.t(10)};
    transform-origin: ${props.vpos === "top" ? "bottom" : "top"}
      ${props.hpos === "right" ? "right" : "left"};
    z-index: 19;
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    position: absolute;
    overflow: hidden;
    user-select: none;
    ${modeStyles}
  `;
});

Styled.DropdownContent = styled.div`
  label: TextDropdownMenu_DropdownContent;
  width: 100%;
  max-height: var(--menu-max-height, 300px);
  display: flex;
  flex-direction: column;
  min-height: 0;
`;
